import type { TreatmentPrescription } from '../../store/treatmentsSlice'

interface PrescribedMedicationsProps {
  prescriptions: TreatmentPrescription[]
  onCreatePrescription: () => void
}

export function PrescribedMedications({ prescriptions, onCreatePrescription }: PrescribedMedicationsProps) {
  return (
    <div className="bg-surface rounded-[6px] border border-border-light" data-testid="prescribed-medications-section">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-light">
        <h2 className="text-[14px] font-semibold text-text-primary">Prescribed Medications</h2>
        <button
          data-testid="add-prescription-button"
          onClick={onCreatePrescription}
          className="inline-flex items-center h-7 px-3 rounded-[4px] bg-accent text-white text-[13px] font-normal hover:bg-accent-dark transition-colors duration-100 cursor-pointer"
        >
          Add Prescription
        </button>
      </div>

      <div className="p-4 max-sm:p-3">
        {prescriptions.length === 0 ? (
          <p className="text-[13px] text-text-muted" data-testid="prescribed-medications-empty">
            No medications prescribed yet
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {prescriptions.map((rx) => {
              const rxNum = `RX-${String(rx.prescription_number).padStart(4, '0')}`
              return (
                <div
                  key={rx.id}
                  className="flex items-center gap-3 py-2 px-3 rounded-[4px] hover:bg-hover"
                  data-testid="prescribed-medication-item"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[13px] font-medium text-text-primary" data-testid="rx-medication-name">
                        {rx.medication_name}
                      </span>
                      {rx.dosage && (
                        <span className="text-[12px] text-text-muted" data-testid="rx-dosage">
                          {rx.dosage}
                        </span>
                      )}
                      {rx.frequency && (
                        <span className="text-[12px] text-text-muted" data-testid="rx-frequency">
                          {rx.frequency}
                        </span>
                      )}
                      {rx.duration && (
                        <span className="text-[12px] text-text-muted" data-testid="rx-duration">
                          {rx.duration}
                        </span>
                      )}
                    </div>
                    <span className="text-[11px] text-text-disabled">{rxNum}</span>
                  </div>
                  <span
                    data-testid="rx-status-badge"
                    className={`inline-flex items-center h-5 px-1.5 rounded-[3px] text-[11px] font-medium ${
                      rx.status === 'Active'
                        ? 'bg-accent-subtle-bg text-status-success'
                        : 'bg-border text-text-muted'
                    }`}
                  >
                    {rx.status}
                  </span>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
